// WF-CONTROL in the in-memory API (services/api/src/routes/control.ts, auth/control-tokens.ts,
// domain/org/profile-revisions.ts), with the real routes' rules:
//   - `control.tokens.list`: the caller's own connections (every company's), newest first: every
//     unrevoked, unexpired one, plus the newest 50 revoked or expired as history; with the limit
//     (5 live or pending per person);
//   - `control.tokens.create`: the `workforce_plugin` add-on first (its closure: `agents` and
//     `memory` too; 403 `feature_disabled`, or `feature_read_only` in grace), then a manager
//     (owner, admin or the agency operator; else 403), the contract's body (422), the step-up (a
//     person with two-factor sign-in sends a current `totp_code`, else 403 `mfa_code_required`;
//     a bucket of 5 tries refilling fully in 15 minutes, then 429), the limit (409 `conflict`); the code (XXXX-XXXX-XXXX,
//     no 0/O/1/I/L) is answered once, 10 minutes;
//   - `control.tokens.revoke`: the caller's own token only (else 404); already revoked = no-op;
//   - `control.pair`: no session; the code once → `cosk_ctl_…` (every failure the same 404);
//     `Accept: text/plain` answers the bare token;
//   - `workforcePlugin.get`/`.put`: the add-on lives in `/v1/me`'s entitlements (every account's
//     map is switched, as the ops twin's `setStatus`); an owner or the agency operator switches it
//     (a manager otherwise 403); enabling needs `agents` (409 `addon_requires`); as asked = 200
//     (on: active or trial; off: disabled only, so switching off in grace writes `disabled`).
//   - profile revisions of a DE and an agent: the DE and agent twins call `revise` on create and
//     on a PATCH that changes role, goal, backstory, standing orders (and an agent's
//     responsibilities), `via: "app"`; the skill pack twin calls `reviseSkills` for each DE whose
//     skills a pack's role or archive change moved. Nothing syncs in phase 1: `runtime.unsynced` is true as
//     soon as a revision exists.
import { routes } from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { featureClosure } from "../features/addons/catalog";
import { featureDisabled, json, notFound, type Principal, page, problem, validation } from "./fake";
import type { ComposedHandler } from "./fake-merge";
import type { EntitlementStatus, MeResponse } from "./types";

type TokenView = OperationOutput<"control.tokens.revoke">;
type Revision = OperationOutput<"digitalEmployees.profileRevisions">["data"][number];
type PluginState = OperationOutput<"workforcePlugin.get">;

export const FAKE_CONTROL_ROUTES = [
  "control.tokens.list",
  "control.tokens.create",
  "control.tokens.revoke",
  "control.pair",
  "workforcePlugin.get",
  "workforcePlugin.put",
  "digitalEmployees.profileRevisions",
  "workforceAgents.profileRevisions",
] as const satisfies readonly RouteId[];

export const CONTROL_TOKEN_PREFIX = "cosk_ctl_";
export const MAX_LIVE_TOKENS = 5;
const PAIR_TTL_MS = 10 * 60_000;
const TOKEN_TTL_MS = 90 * 86_400_000;
export const HISTORY_LIMIT = 50;
/** The step-up's bucket per person (a well-formed code spends one): capacity 5, +1 per 180 s. */
const STEP_UP_CAPACITY = 5;
const STEP_UP_REFILL_MS = 180_000;
const CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const BASE62 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

export const DE_PROFILE_FIELDS = ["role", "goal", "backstory", "standing_orders"] as const;
export const AG_PROFILE_FIELDS = [...DE_PROFILE_FIELDS, "responsibilities"] as const;
export type RevisionSubject = "digital_employee" | "workforce_agent";

function randomOf(alphabet: string, n: number): string {
  const bytes = new Uint8Array(n);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => alphabet[b % alphabet.length]).join("");
}

/** A typed code in its canonical form (case, spaces and dashes forgiven); null if malformed. */
export function normalizeCode(code: string): string | null {
  const c = code.toUpperCase().replace(/[\s-]/g, "");
  if (c.length !== 12) return null;
  for (const ch of c) if (!CODE_ALPHABET.includes(ch)) return null;
  return c;
}

type TokenRow = {
  id: string;
  userId: string;
  companyId: string;
  label: string;
  code: string | null;
  pairExpiresAt: Date;
  pairedAt: Date | null;
  keyPrefix: string | null;
  expiresAt: Date;
  lastUsedAt: Date | null;
  revokedAt: Date | null;
  mfa: boolean;
  createdAt: Date;
};

const ON = (s: EntitlementStatus | undefined) => s === "active" || s === "trial";

export function createControlFake(deps: {
  now: () => Date;
  /** Every account's `/v1/me` (an add-on switch shows on each one's next read). */
  accounts: () => MeResponse[];
  /** The ops twin's ledger row for an owner's switch (when its rows were read already). */
  recordSwitch?: (
    companyId: string,
    status: EntitlementStatus,
    userId: string,
    note: string,
  ) => void;
  /** The DE is readable to the caller (production's `readableDe`). */
  deReadable: (p: Principal, id: string) => boolean;
  /** The agent exists in the company. */
  agentExists: (p: Principal, id: string) => boolean;
  /** The person's two-factor sign-in (fake-mfa.ts): enrolled, and the step-up's code check. */
  twoFactor: {
    enrolled: (email: string) => boolean;
    verify: (email: string, code: string) => Promise<boolean>;
  };
}) {
  const tokens: TokenRow[] = [];
  const stepUpTries = new Map<string, { tokens: number; at: number }>();
  const revisions = new Map<string, Revision[]>();
  let seq = 0;
  let revSeq = 0;
  const iso = (d: Date | null) => (d ? d.toISOString() : null);

  const stateOf = (r: TokenRow, now: Date): TokenView["state"] => {
    if (r.revokedAt) return "revoked";
    if (!r.pairedAt) return r.pairExpiresAt > now ? "pending" : "expired";
    return r.expiresAt > now ? "live" : "expired";
  };
  const viewOf = (r: TokenRow, now: Date): TokenView => ({
    id: r.id,
    label: r.label,
    state: stateOf(r, now),
    key_prefix: r.keyPrefix,
    created_at: r.createdAt.toISOString(),
    pair_expires_at: r.pairedAt ? null : r.pairExpiresAt.toISOString(),
    paired_at: iso(r.pairedAt),
    expires_at: r.expiresAt.toISOString(),
    last_used_at: iso(r.lastUsedAt),
    revoked_at: iso(r.revokedAt),
    mfa: r.mfa,
  });
  const liveOrPending = (r: TokenRow, now: Date) => {
    const s = stateOf(r, now);
    return s === "live" || s === "pending";
  };

  /** `requireFeature`'s verdict over the feature's closure, from `/v1/me`. */
  const verdict = (me: MeResponse, companyId: string, feature: "workforce_plugin" | "agents") => {
    const map = me.entitlements[companyId] ?? {};
    let grace = false;
    for (const k of featureClosure(feature)) {
      const s = map[k] ?? "disabled";
      if (s === "disabled") return "off" as const;
      if (s === "grace") grace = true;
    }
    return grace ? ("grace" as const) : ("on" as const);
  };

  const maySwitch = (p: Principal) => p.operator || p.roles.includes("owner");
  const pluginState = (p: Principal): PluginState => ({
    status: p.me.entitlements[p.company.id]?.workforce_plugin ?? "disabled",
    agents_on: verdict(p.me, p.company.id, "agents") !== "off",
    agents_writable: verdict(p.me, p.company.id, "agents") === "on",
    can_switch: maySwitch(p),
    can_use: p.manager,
  });

  const parse = (id: RouteId, body: unknown): Record<string, unknown> | Response => {
    const schema = (
      routes[id] as unknown as {
        body: {
          safeParse: (v: unknown) => {
            success: boolean;
            data?: unknown;
            error?: { issues: { path: PropertyKey[]; message: string }[] };
          };
        };
      }
    ).body;
    const r = schema.safeParse(body ?? {});
    if (!r.success) {
      const i = r.error?.issues[0];
      return validation(
        i ? `${i.path.map(String).join(".") || "body"}: ${i.message}` : "invalid body",
      );
    }
    return r.data as Record<string, unknown>;
  };

  const revisionsPage = (kind: RevisionSubject, p: Principal, id: string, url: URL) => {
    const all = revisions.get(`${p.company.id}|${kind}|${id}`) ?? [];
    const newest = [...all].reverse();
    const latest = all.at(-1)?.n ?? null;
    const synced = [...all].reverse().find((r) => r.synced_at)?.n ?? null;
    return json({
      ...page(newest, url),
      runtime: {
        latest_n: latest,
        synced_n: synced,
        unsynced: latest !== null && latest !== synced,
      },
    });
  };

  const handlers: Partial<Record<RouteId, ComposedHandler>> = {
    "control.tokens.list": (ctx) => {
      if (!ctx.principal) return notFound();
      const now = deps.now();
      // `listControlTokens`: every unrevoked, unexpired one, plus the newest 50 others as history.
      const newest = (a: TokenRow, b: TokenRow) => b.createdAt.getTime() - a.createdAt.getTime();
      const mine = tokens.filter((t) => t.userId === ctx.me.user.id);
      const open = (t: TokenRow) => !t.revokedAt && t.expiresAt > now;
      const history = mine
        .filter((t) => !open(t))
        .sort(newest)
        .slice(0, HISTORY_LIMIT);
      const rows = [...mine.filter(open), ...history].sort(newest);
      return json({ data: rows.map((t) => viewOf(t, now)), limit: MAX_LIVE_TOKENS });
    },
    "control.tokens.create": async (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      // The add-on gate first (the route's `feature()` middleware), then `authorize(manager)`.
      const gate = verdict(p.me, p.company.id, "workforce_plugin");
      if (gate === "off") return featureDisabled("workforce_plugin");
      if (gate === "grace")
        return problem(
          403,
          "feature_read_only",
          "Read-only",
          "The workforce_plugin add-on is in its grace period.",
          { feature: "workforce_plugin" },
        );
      if (!p.manager) return problem(403, "forbidden", "Forbidden");
      const body = parse("control.tokens.create", ctx.body);
      if (body instanceof Response) return body;
      const now = deps.now();
      const userId = p.me.user.id;
      // The step-up: an enrolled person sends a current code (verified on this request; 10 tries
      // per 15 minutes); a person without two-factor sign-in mints without one (`mfa: false`).
      const email = p.me.user.email;
      const mfa = deps.twoFactor.enrolled(email);
      if (mfa) {
        const code = typeof body.totp_code === "string" ? body.totp_code : null;
        if (code === null)
          return problem(403, "mfa_code_required", "A current two-factor code is needed");
        // The server's STEP_UP_TRIES bucket: capacity 5, refilled fully in 900 s.
        const t = now.getTime();
        const prev = stepUpTries.get(userId) ?? { tokens: STEP_UP_CAPACITY, at: t };
        const tokens = Math.min(
          STEP_UP_CAPACITY,
          prev.tokens + Math.max(0, t - prev.at) / STEP_UP_REFILL_MS,
        );
        if (tokens < 1) {
          stepUpTries.set(userId, { tokens, at: t });
          return problem(429, "rate_limited", "Too many requests", undefined, {
            retry_after: Math.ceil(((1 - tokens) * STEP_UP_REFILL_MS) / 1000),
          });
        }
        stepUpTries.set(userId, { tokens: tokens - 1, at: t });
        if (!(await deps.twoFactor.verify(email, code)))
          return problem(403, "mfa_code_required", "A current two-factor code is needed");
      }
      if (
        tokens.filter((t) => t.userId === userId && liveOrPending(t, now)).length >= MAX_LIVE_TOKENS
      )
        return problem(
          409,
          "conflict",
          "Conflict",
          `at most ${MAX_LIVE_TOKENS} live connections: revoke one first`,
        );
      const code = randomOf(CODE_ALPHABET, 12);
      const row: TokenRow = {
        id: `0199f2c0-0c71-7000-8000-${String(++seq).padStart(12, "0")}`,
        userId,
        companyId: p.company.id,
        label: String(body.label),
        code,
        pairExpiresAt: new Date(now.getTime() + PAIR_TTL_MS),
        pairedAt: null,
        keyPrefix: null,
        expiresAt: new Date(now.getTime() + TOKEN_TTL_MS),
        lastUsedAt: null,
        revokedAt: null,
        mfa,
        createdAt: now,
      };
      tokens.push(row);
      const view = viewOf(row, now);
      return json(
        {
          token: view,
          pair_code: `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}`,
          pair_expires_at: view.pair_expires_at as string,
        },
        201,
      );
    },
    "control.tokens.revoke": (ctx) => {
      if (!ctx.principal) return notFound();
      const row = tokens.find((t) => t.id === ctx.params.id && t.userId === ctx.me.user.id);
      if (!row) return notFound();
      const now = deps.now();
      if (!row.revokedAt) {
        row.revokedAt = now;
        row.code = null;
      }
      return json(viewOf(row, now));
    },
    // No principal: the dispatcher hands this one over before its sign-in check.
    "control.pair": (ctx) => {
      const raw = (ctx.body as { code?: unknown } | null)?.code;
      if (typeof raw !== "string" || !raw.trim() || raw.trim().length > 64)
        return validation("code: required");
      const canonical = normalizeCode(raw);
      const now = deps.now();
      const row = canonical
        ? tokens.find(
            (t) =>
              t.code === canonical &&
              !t.revokedAt &&
              !t.pairedAt &&
              t.pairExpiresAt > now &&
              t.expiresAt > now,
          )
        : undefined;
      if (!row) return notFound();
      const token = `${CONTROL_TOKEN_PREFIX}${randomOf(BASE62, 40)}`;
      row.code = null;
      row.pairedAt = now;
      row.keyPrefix = token.slice(0, 16);
      if ((ctx.headers.get("accept") ?? "").includes("text/plain"))
        return new Response(token, {
          status: 200,
          headers: { "content-type": "text/plain; charset=utf-8", "cache-control": "no-store" },
        });
      return json({ token, token_id: row.id, expires_at: row.expiresAt.toISOString() });
    },
    "workforcePlugin.get": (ctx) => {
      if (!ctx.principal) return notFound();
      return json(pluginState(ctx.principal));
    },
    "workforcePlugin.put": (ctx) => {
      const p = ctx.principal;
      if (!p) return notFound();
      if (!p.manager || !maySwitch(p)) return problem(403, "forbidden", "Forbidden");
      const body = parse("workforcePlugin.put", ctx.body);
      if (body instanceof Response) return body;
      const enabled = body.enabled === true;
      const previous = p.me.entitlements[p.company.id]?.workforce_plugin ?? "disabled";
      // Already as asked: on is active or trial; off is disabled only (switching off in grace
      // writes `disabled`: a grace period still lets reads through).
      if (enabled ? ON(previous) : previous === "disabled") return json(pluginState(p));
      if (enabled && verdict(p.me, p.company.id, "agents") !== "on")
        return problem(409, "addon_requires", "Add-on requires another add-on", undefined, {
          feature: "workforce_plugin",
          requires: ["agents"],
        });
      const status: EntitlementStatus = enabled ? "active" : "disabled";
      for (const me of deps.accounts()) {
        const map = me.entitlements[p.company.id];
        if (map) map.workforce_plugin = status;
      }
      deps.recordSwitch?.(
        p.company.id,
        status,
        p.me.user.id,
        p.operator ? "Switched by the agency operator (Setup)" : "Switched by an owner (Setup)",
      );
      return json(pluginState(p));
    },
    "digitalEmployees.profileRevisions": (ctx) => {
      const p = ctx.principal;
      if (!p || !ctx.params.id || !deps.deReadable(p, ctx.params.id)) return notFound();
      return revisionsPage("digital_employee", p, ctx.params.id, ctx.url);
    },
    "workforceAgents.profileRevisions": (ctx) => {
      const p = ctx.principal;
      if (!p || !ctx.params.id || !deps.agentExists(p, ctx.params.id)) return notFound();
      return revisionsPage("workforce_agent", p, ctx.params.id, ctx.url);
    },
  };

  /**
   * Records revision n+1 of a DE or an agent when a profile field changed (production's
   * `profileChanges` + `recordProfileRevision`); `before` null = a new worker (from null).
   */
  function revise(
    p: Principal,
    kind: RevisionSubject,
    subjectId: string,
    before: Record<string, unknown> | null,
    after: Record<string, unknown>,
  ): void {
    const fields = kind === "digital_employee" ? DE_PROFILE_FIELDS : AG_PROFILE_FIELDS;
    const empty = (f: string) => (f === "responsibilities" ? [] : null);
    const snapshot: Record<string, unknown> = {};
    const changes: Record<string, { from: unknown; to: unknown }> = {};
    for (const f of fields) {
      const to = after[f] ?? empty(f);
      const from = before ? (before[f] ?? empty(f)) : empty(f);
      snapshot[f] = to;
      if (JSON.stringify(from) !== JSON.stringify(to)) changes[f] = { from, to };
    }
    if (!Object.keys(changes).length) return;
    record(p, kind, subjectId, snapshot, changes);
  }

  /**
   * A DE's skills changed (a pack's roles or archive state; production's `reviseSkills` in
   * domain/skill-packs/skill-revisions.ts): a `skills` revision, before → after.
   */
  function reviseSkills(p: Principal, deId: string, from: string[], to: string[]): void {
    if (JSON.stringify(from) === JSON.stringify(to)) return;
    record(p, "digital_employee", deId, { skills: to }, { skills: { from, to } });
  }

  /** Appends revision n+1 (production's `recordProfileRevision`), `via: "app"`. */
  function record(
    p: Principal,
    kind: RevisionSubject,
    subjectId: string,
    snapshot: Record<string, unknown>,
    changes: Record<string, { from: unknown; to: unknown }>,
  ): void {
    const key = `${p.company.id}|${kind}|${subjectId}`;
    const list = revisions.get(key) ?? [];
    const self = p.me.memberships.find((m) => m.company_id === p.company.id);
    list.push({
      id: `0199f2c1-0e71-7000-8000-${String(++revSeq).padStart(12, "0")}`,
      subject_kind: kind,
      subject_id: subjectId,
      n: list.length + 1,
      fields: snapshot,
      changes,
      actor: {
        kind: self ? "person" : "agency_user",
        membership_id: self?.membership_id ?? null,
        user_id: p.me.user.id,
        name: self ? p.me.user.name : null,
      },
      via: "app",
      control_token_id: null,
      synced_at: null,
      created_at: deps.now().toISOString(),
    });
    revisions.set(key, list);
  }

  return { handlers, revise, reviseSkills };
}

export type ControlFake = ReturnType<typeof createControlFake>;
